import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { UseQueryWrapper, User } from '../../../types';

import type { GetUsersParams } from '../../../Domain/Users/Services';
import { userService } from '../../../Domain/Users/Services';

import { useSnakeBar } from '../../useSnakeBar';

export type GetUsersQueryVariables = GetUsersParams;

export type GetUsersQueryResultData = {
  data: Array<User>;
  totalCount: number;
  internal?: boolean;
};

const queryFn = (variables?: GetUsersQueryVariables) => async () => {
  const response = await userService.getUsers(variables);
  return (response as AxiosResponse).data;
};

const QUERY_KEY = 'getUsers';

export const useUserGetAllQuery: UseQueryWrapper<GetUsersQueryResultData, GetUsersQueryVariables, false> = ({
  variables,
  options,
}) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  const select = React.useCallback(data => {
    return {
      data: data['hydra:member'],
      totalCount: data['hydra:totalItems'],
    };
  }, []);

  return useQuery<GetUsersQueryResultData>([QUERY_KEY, ...Object.values(variables || {})], queryFn(variables), {
    select,
    onError,
    ...(options || {}),
  });
};
